import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import spiritualGiftsShortSample from '../data/test-samples/spiritual-gifts-short-sample.json';
import spiritualGiftsFullSample from '../data/test-samples/spiritual-gifts-full-sample.json';
import orientationConscienceSample from '../data/test-samples/orientation-conscience-sample.json';
import { calculateQuestionnaireResults, getQuestionnaireType } from '../utils/questionnaireTypes';

const TestResultsPage = () => {
  const navigate = useNavigate();
  const [selectedSample, setSelectedSample] = useState('');
  const [customJson, setCustomJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [testEmail, setTestEmail] = useState('test@example.com');
  const [isTestingPdf, setIsTestingPdf] = useState(false);
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [testResults, setTestResults] = useState('');

  const sampleData = {
    'spiritual-gifts-short': {
      name: '🟢 Dons Spirituels - Version Courte (84 questions)',
      data: spiritualGiftsShortSample
    },
    'spiritual-gifts-full': {
      name: '🔵 Dons Spirituels - Version Complète (168 questions)',
      data: spiritualGiftsFullSample
    },
    'orientation-conscience': {
      name: '🟣 Orientation de la Conscience (48 questions)',
      data: orientationConscienceSample
    }
  };

  const loadSampleData = () => {
    if (!selectedSample) {
      alert('Veuillez sélectionner un échantillon à charger.');
      return;
    }

    const sample = sampleData[selectedSample];
    if (sample) {
      navigateToResults(sample.data);
    }
  };

  const loadCustomJson = () => {
    if (!customJson.trim()) {
      alert('Veuillez coller du JSON valide.');
      return;
    }

    try {
      const parsedData = JSON.parse(customJson);
      
      // Validate required fields
      if (!parsedData.questionnaireId || !parsedData.answers) {
        throw new Error('Le JSON doit contenir au minimum "questionnaireId" et "answers"');
      }

      setJsonError('');
      navigateToResults(parsedData);
    } catch (error) {
      setJsonError(`Erreur JSON: ${error.message}`);
    }
  };

  const navigateToResults = (data) => {
    // Store test data in localStorage temporarily
    localStorage.setItem('testResultsData', JSON.stringify(data));
    
    // Navigate to results page with test flag
    navigate(`/results/${data.questionnaireId}?test=true`);
  };

  const copyToClipboard = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    alert('JSON copié dans le presse-papiers !');
  };

  // Test the new generateAndEmailPdf endpoint
  const testPdfGeneration = async (sampleKey) => {
    if (!sampleKey) {
      alert('Veuillez sélectionner un échantillon à tester.');
      return;
    }

    setIsTestingPdf(true);
    setTestResults('');

    try {
      const sample = sampleData[sampleKey];
      const questionnaireConfig = getQuestionnaireType(sample.data.questionnaireId);
      
      if (!questionnaireConfig) {
        throw new Error(`Configuration non trouvée pour ${sample.data.questionnaireId}`);
      }

      // Calculate results using the same logic as ResultsPage
      const calculatedResults = calculateQuestionnaireResults(sample.data.questionnaireId, sample.data.answers);
      
      // Prepare data for server
      let resultsData;
      if (questionnaireConfig.resultDisplayType === 'radar' && calculatedResults.scores) {
        resultsData = calculatedResults.scores.map((score, index) => ({
          category: index + 1,
          name: score.name,
          score: score.score,
          total: score.total || questionnaireConfig.scaleMax,
          count: score.count || 1
        }));
      } else if (questionnaireConfig.resultDisplayType === 'scatter' && calculatedResults.results) {
        resultsData = calculatedResults.results.map((result, index) => ({
          category: index + 1,
          name: result.name,
          score: result.average,
          total: 7,
          count: result.count || 1
        }));
      } else {
        resultsData = [];
      }

      let typstError = null;
      
      // TRY 1: High-quality Typst PDF generation (primary)
      try {
        console.log('🎯 Attempting Typst PDF generation for test...');
        
        const typstApiUrl = process.env.NODE_ENV === 'development'
          ? 'http://localhost:7071/api/generateTypstPdf'
          : '/api/generateTypstPdf';
        
        await axios.post(typstApiUrl, {
          email: testEmail,
          userId: 'test-user',
          questionnaireId: questionnaireConfig.id,
          userName: sample.data.userName || 'Utilisateur Test',
          results: resultsData,
          answers: sample.data.answers,
          questionnaireConfig: questionnaireConfig,
          scatterData: calculatedResults.scatterData || null
        }, {
          responseType: 'blob',
          withCredentials: false, // Important for CORS in dev
          timeout: 30000 // 30 second timeout for Typst generation
        });
        
        console.log('✅ Typst PDF generated successfully for test');
        setTestResults('✅ Test PDF Typst généré avec succès ! Fichier prêt pour le téléchargement.');
        return; // Exit successfully
        
      } catch (error) {
        typstError = error;
        console.error('Typst PDF generation failed:', typstError.message);
      }

      // No fallback needed - rely on Typst only
      setTestResults(`❌ La génération PDF Typst a échoué:

Erreur: ${typstError?.message || 'Inconnue'}

Détails: ${typstError.response?.data ? JSON.stringify(typstError.response.data, null, 2) : 'Aucun détail disponible'}`);
      
    } catch (error) {
      console.error('Erreur test PDF:', error);
      setTestResults(`❌ Erreur lors du test PDF:\n\n${error.message}\n\nDétails: ${error.response?.data ? JSON.stringify(error.response.data, null, 2) : 'Aucun détail disponible'}`);
    } finally {
      setIsTestingPdf(false);
    }
  };

  // Test email sending only (for comparison)
  const testEmailOnly = async (sampleKey) => {
    if (!sampleKey) {
      alert('Veuillez sélectionner un échantillon à tester.');
      return;
    }

    setIsTestingEmail(true);
    setTestResults('');

    try {
      const sample = sampleData[sampleKey];
      const questionnaireConfig = getQuestionnaireType(sample.data.questionnaireId);
      const calculatedResults = calculateQuestionnaireResults(sample.data.questionnaireId, sample.data.answers);

      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/sendResultsEmail'
        : '/api/sendResultsEmail';

      const response = await axios.post(apiUrl, {
        email: testEmail,
        results: calculatedResults,
        questionnaireId: questionnaireConfig.id,
        userName: sample.data.userName || 'Utilisateur Test',
        questionnaireConfig: questionnaireConfig
      });

      setTestResults(`✅ Test Email seul réussi!\n\nRéponse: ${JSON.stringify(response.data, null, 2)}`);
      
    } catch (error) {
      console.error('Erreur test Email:', error);
      setTestResults(`❌ Erreur lors du test Email:\n\n${error.message}\n\nDétails: ${error.response?.data ? JSON.stringify(error.response.data, null, 2) : 'Aucun détail disponible'}`);
    } finally {
      setIsTestingEmail(false);
    }
  };

  // Simplified return for debugging
  return (
    <div style={{ padding: '20px' }}>
      <h1>🧪 Test Page - Simplified</h1>
      <p>If you can see this, the component is working.</p>
      
      <div className="container" style={{ marginTop: '2rem' }}>
        <div className="card">
          <h1 style={{ color: '#2c3e50', marginBottom: '2rem', textAlign: 'center' }}>
            🧪 Page de Test - Rapports et PDFs
          </h1>
          
        <div style={{ 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7', 
          borderRadius: '8px', 
          padding: '1rem', 
          marginBottom: '2rem' 
        }}>
          <strong>🔧 Mode Test :</strong> Cette page permet de tester la génération de rapports et PDFs 
          sans avoir à remplir manuellement les questionnaires.
        </div>

        {/* Section 1: Échantillons prédéfinis */}
        <div style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            📋 Échantillons Prédéfinis
          </h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Sélectionner un échantillon :
            </label>
            <select
              value={selectedSample}
              onChange={(e) => setSelectedSample(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            >
              <option value="">-- Choisir un échantillon --</option>
              {Object.entries(sampleData).map(([key, sample]) => (
                <option key={key} value={key}>
                  {sample.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={loadSampleData}
              disabled={!selectedSample}
              className="btn btn-primary"
              style={{ flex: 1, minWidth: '200px' }}
            >
              🚀 Charger et Voir le Rapport
            </button>
            
            {selectedSample && (
              <button
                onClick={() => copyToClipboard(sampleData[selectedSample].data)}
                className="btn btn-secondary"
                style={{ flex: 1, minWidth: '200px' }}
              >
                📋 Copier JSON
              </button>
            )}
          </div>
        </div>

        {/* Section 2: Test nouveaux endpoints */}
        <div style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            🚀 Test Endpoints PDF et Email  
          </h2>
          
          <div style={{ 
            backgroundColor: '#e8f5e8', 
            border: '1px solid #d4edda', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1.5rem' 
          }}>
            <strong>🧪 Tests automatisés :</strong> Testez directement les nouveaux endpoints 
            <code>generateAndEmailPdf</code> et <code>sendResultsEmail</code> avec les échantillons sélectionnés.
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Email de test :
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ 
            backgroundColor: '#f0f8ff', 
            border: '1px solid #b0d4f1', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginBottom: '1rem' 
          }}>
            <strong>🔧 Mode de génération PDF :</strong> 
            Typst (haute qualité) avec fallback automatique vers jsPDF
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <button
              onClick={() => testPdfGeneration(selectedSample)}
              disabled={!selectedSample || isTestingPdf || !testEmail}
              className="btn btn-success"
              style={{ flex: 1, minWidth: '200px' }}
            >
              {isTestingPdf ? '⏳ Test en cours...' : '🎭 Test PDF + Email (Avec Fallback)'}
            </button>
            
            <button
              onClick={() => testEmailOnly(selectedSample)}
              disabled={!selectedSample || isTestingEmail || !testEmail}
              className="btn btn-warning"
              style={{ flex: 1, minWidth: '200px' }}
            >
              {isTestingEmail ? '⏳ Test en cours...' : '📨 Test Email seul'}
            </button>
          </div>

          {testResults && (
            <div style={{
              backgroundColor: testResults.startsWith('✅') ? '#d4edda' : '#f8d7da',
              border: `1px solid ${testResults.startsWith('✅') ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <pre style={{ 
                margin: 0, 
                fontSize: '0.9rem',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace'
              }}>
                {testResults}
              </pre>
            </div>
          )}
        </div>

        {/* Section 3: JSON personnalisé */}
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            📝 JSON Personnalisé
          </h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              Coller votre JSON de test :
            </label>
            <textarea
              value={customJson}
              onChange={(e) => setCustomJson(e.target.value)}
              placeholder={`Exemple:
{
  "questionnaireId": "dons-spirituels-court",
  "questionnaireType": "spiritual-gifts",
  "userName": "Test User",
  "totalQuestions": 84,
  "answers": {
    "0": 3,
    "1": 2,
    ...
  },
  "timestamp": "2025-07-19T00:00:00.000Z"
}`}
              style={{
                width: '100%',
                height: '200px',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                resize: 'vertical'
              }}
            />
          </div>

          {jsonError && (
            <div style={{ 
              color: '#e74c3c', 
              backgroundColor: '#fdf2f2', 
              padding: '0.75rem', 
              borderRadius: '4px', 
              marginBottom: '1rem',
              border: '1px solid #e74c3c'
            }}>
              {jsonError}
            </div>
          )}

          <button
            onClick={loadCustomJson}
            disabled={!customJson.trim()}
            className="btn btn-success"
            style={{ width: '100%' }}
          >
            📊 Charger JSON Personnalisé
          </button>
        </div>

        {/* Section 4: Format JSON attendu */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: '#2c3e50', marginBottom: '1rem' }}>
            📖 Format JSON Attendu
          </h3>
          
          <div style={{ marginBottom: '1rem' }}>
            <strong>Champs obligatoires :</strong>
            <ul style={{ marginTop: '0.5rem' }}>
              <li><code>questionnaireId</code> : ID du questionnaire (ex: "dons-spirituels-court")</li>
              <li><code>answers</code> : Objet avec les réponses indexées</li>
            </ul>
          </div>

          <div>
            <strong>Champs optionnels :</strong>
            <ul style={{ marginTop: '0.5rem' }}>
              <li><code>questionnaireType</code> : Type du questionnaire</li>
              <li><code>userName</code> : Nom de l'utilisateur</li>
              <li><code>totalQuestions</code> : Nombre total de questions</li>
              <li><code>timestamp</code> : Date de complétion</li>
            </ul>
          </div>
        </div>

        {/* Navigation */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => navigate('/')}
            className="btn btn-secondary"
          >
            🏠 Retour à l'accueil
          </button>
        </div>
      </div>
    </div>
    </div>
  );
};

export default TestResultsPage;